"use client";

import { useMemo, useState } from "react";
import {
  Field,
  TextInput,
  TextArea,
  SegmentedControl,
  ResultBanner,
  PrintButton,
  PrintFooter,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* A work-related stress risk assessment around the HSE Management Standards — the
   six areas (demands, control, support, relationships, role, change) employers are
   expected to assess to meet their duty under the Health and Safety at Work etc. Act
   1974 and the Management of H&S at Work Regulations 1999. Same identify–evaluate–
   control sequence as any risk assessment, applied to psychosocial hazards.

   Compliance (YMYL): a self-help record. Stress is best assessed WITH employees
   (e.g. the HSE indicator tool / staff survey); this drafts findings and actions and
   does not replace consultation or professional support where needed. */

type Rating = "" | "good" | "concern" | "action";

interface Standard { id: string; title: string; prompt: string }
const STANDARDS: Standard[] = [
  { id: "demands", title: "Demands", prompt: "Workload, work patterns, deadlines and the work environment — are demands achievable in agreed hours, with adequate breaks and a suitable environment?" },
  { id: "control", title: "Control", prompt: "How much say people have over how and when they do their work, and over the pace of work." },
  { id: "support", title: "Support", prompt: "Encouragement, sponsorship and resources from the organisation and from line managers, and access to help when needed." },
  { id: "relationships", title: "Relationships", prompt: "Positive working to avoid conflict, and fair, firm handling of unacceptable behaviour such as bullying or harassment." },
  { id: "role", title: "Role", prompt: "Whether people understand their role and responsibilities and the organisation avoids conflicting roles." },
  { id: "change", title: "Change", prompt: "How organisational change (large or small) is managed and communicated, and how people are supported through it." },
];

const OPTIONS = [
  { value: "good" as const, label: "Working well", activeCls: "border-emerald bg-emerald-tint text-emerald-deep" },
  { value: "concern" as const, label: "Some concern", activeCls: "border-amber-400 bg-amber-50 text-amber-800" },
  { value: "action" as const, label: "Needs action", activeCls: "border-risk-veryhigh bg-risk-veryhigh/10 text-risk-veryhigh" },
];

export function StressRiskAssessment() {
  const [org, setOrg] = useState("");
  const [team, setTeam] = useState("");
  const [assessor, setAssessor] = useState("");
  const [ratings, setRatings] = useState<Record<string, Rating>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});

  const result = useMemo(() => {
    const answered = STANDARDS.filter((s) => ratings[s.id]);
    const action = STANDARDS.filter((s) => ratings[s.id] === "action");
    const concern = STANDARDS.filter((s) => ratings[s.id] === "concern");
    const remaining = STANDARDS.length - answered.length;
    let level: Verdict = "low";
    let title = "";
    if (answered.length === 0) title = "Rate each of the six Management Standards";
    else if (action.length > 0) { level = action.length >= 2 ? "veryhigh" : "medium"; title = `${action.length} area${action.length > 1 ? "s" : ""} need action`; }
    else if (concern.length > 0) { level = "medium"; title = `${concern.length} area${concern.length > 1 ? "s" : ""} to keep an eye on`; }
    else if (remaining === 0) { level = "low"; title = "All six areas working well on this assessment"; }
    else { level = "low"; title = `${answered.length}/6 areas assessed`; }
    return { action, concern, remaining, answered: answered.length, level, title };
  }, [ratings]);

  if (result.remaining === 0 && result.answered > 0) trackToolResult("stress-risk-assessment", { action: result.action.length, concern: result.concern.length });

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Organisation"><TextInput value={org} onChange={(e) => setOrg(e.target.value)} placeholder="e.g. Acme Ltd" /></Field>
        <Field label="Team / area"><TextInput value={team} onChange={(e) => setTeam(e.target.value)} placeholder="e.g. Site team" /></Field>
        <Field label="Assessed by"><TextInput value={assessor} onChange={(e) => setAssessor(e.target.value)} placeholder="Name" /></Field>
      </div>

      {result.answered > 0 && (
        <ResultBanner level={result.level} title={result.title}>
          {result.action.length > 0 ? <p>Where an area needs action, agree specific, prioritised actions <strong>with the people affected</strong> — stress controls only work when staff are involved. Record the action, who owns it and a date.</p>
            : result.concern.length > 0 ? <p>No areas flagged for urgent action, but keep the concerns under review and talk to the team about them before they escalate.</p>
            : <p>The six areas look to be working well. Keep checking in — stress risk changes with workload, people and organisational change. Re-assess regularly and after significant change.</p>}
        </ResultBanner>
      )}

      <div className="space-y-4">
        {STANDARDS.map((s) => (
          <fieldset key={s.id} className="rounded-2xl border border-line bg-white p-5 shadow-soft">
            <legend className="px-2 font-display text-base font-bold text-ink">{s.title}</legend>
            <p className="mt-2 text-sm text-muted">{s.prompt}</p>
            <div className="mt-4">
              <SegmentedControl legend={s.title} name={s.id} value={ratings[s.id] ?? ""} onChange={(v) => setRatings((r) => ({ ...r, [s.id]: v as Rating }))} options={OPTIONS} />
            </div>
            {ratings[s.id] && ratings[s.id] !== "good" && (
              <div className="mt-3">
                <TextArea value={notes[s.id] ?? ""} onChange={(e) => setNotes((n) => ({ ...n, [s.id]: e.target.value }))} placeholder={`What's the issue, and what action is agreed for ${s.title.toLowerCase()}?`} rows={2} />
              </div>
            )}
          </fieldset>
        ))}
      </div>

      {/* Actions summary (prints) */}
      {(result.action.length > 0 || result.concern.length > 0) && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <h3 className="font-display text-base font-bold text-amber-900">Areas to act on{team && ` · ${team}`}</h3>
          <ul className="mt-3 space-y-2 text-sm text-amber-900">
            {[...result.action, ...result.concern].map((s) => (
              <li key={s.id} className="flex gap-2">
                <span className="mt-0.5 shrink-0 font-bold">{ratings[s.id] === "action" ? "‼" : "•"}</span>
                <span><strong>{s.title}</strong>{ratings[s.id] === "action" && " (needs action)"}{notes[s.id] && ` — ${notes[s.id]}`}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <PrintButton tool="stress-risk-assessment" label="Print / save this assessment" />
        <span className="text-xs text-muted">{result.answered}/6 areas assessed</span>
      </div>
      <PrintFooter toolName="Stress Risk Assessment" />
    </div>
  );
}
